import React, { useMemo, useContext } from 'react';
import styled from 'styled-components';

// import Button from '../../../components/Button';
import { Button, Box, CardContent } from '@material-ui/core';
// import Card from '../../../components/Card';
// import CardContent from '../../../components/CardContent';
import CardIcon from '../../../components/CardIcon';
import { AddIcon, RemoveIcon } from '../../../components/icons';
import FlashOnIcon from '@material-ui/icons/FlashOn';
import IconButton from '../../../components/IconButton';
import AddButton from '../../../components/AddButton';
import Label from '../../../components/Label';
import Value from '../../../components/Value';
import { ThemeContext } from 'styled-components';

import useApprove2, { ApprovalState } from '../../../hooks/useApprove2';
import useModal from '../../../hooks/useModal';
import useStake from '../../../hooks/useStake';
import useZap from '../../../hooks/useZap';
import useStakedBalance from '../../../hooks/useStakedBalance';
import useStakedTokenPriceInDollars from '../../../hooks/useStakedTokenPriceInDollars';
import useTokenBalance from '../../../hooks/useTokenBalance';
import useWithdraw from '../../../hooks/useWithdraw';

import { getDisplayBalance } from '../../../utils/formatBalance';

import DepositModal from './DepositModal';
import WithdrawModal from './WithdrawModal';
import ZapModal from './ZapModal';
import TokenSymbol from '../../../components/TokenSymbol';
import { Bank } from '../../../tomb-finance';
import CardPyramid from '../../../components/CardPyramid';
import ApproveModal from './ApproveModal';

const StyledCard = styled(CardPyramid)`
`;

interface ThemeType {
  color: {
    grey: {
      [key: number]: string;
    };
  };
  spacing: {
    [key: number]: number;
  };
}

interface StakeProps {
  bank: Bank;
}

const Stake: React.FC<StakeProps> = ({ bank }) => {
  const [approveStatus, approve] = useApprove2(bank.depositToken, bank.address);
  const [isApproving, setIsApproving] = React.useState(false);
  const tokenBalance = useTokenBalance(bank.depositToken);

  const [onPresentApprove, onDismissApprove] = useModal(
    <ApproveModal
      max={tokenBalance}
      decimals={bank.depositToken.decimal}
      onConfirm={(amount) => {
        if (Number(amount) <= 0 || isNaN(Number(amount))) return;
        handleApprove(amount);
        onDismissApprove();
      }}
      tokenName={bank.depositTokenName}
    />,
  );

  const handleApprove = async (amount: string) => {
    try {
      setIsApproving(true);
      await approve(amount);
    } catch (error) {
      console.error('Approval error:', error);
    } finally {
      setIsApproving(false);
    }
  };

  const { color: themeColor } = useContext(ThemeContext as React.Context<ThemeType>);
  const stakedBalance = useStakedBalance(bank.contract, bank.poolId);
  const stakedTokenPriceInDollars = useStakedTokenPriceInDollars(bank.depositTokenName, bank.depositToken);
  const tokenPriceInDollars = useMemo(
    () => (stakedTokenPriceInDollars ? stakedTokenPriceInDollars : null),
    [stakedTokenPriceInDollars],
  );
  const earnedInDollars = (
    Number(tokenPriceInDollars) * Number(getDisplayBalance(stakedBalance, bank.depositToken.decimal))
  ).toFixed(2);
  const { onStake } = useStake(bank);
  const { onZap } = useZap(bank);
  const { onWithdraw } = useWithdraw(bank);

  const [onPresentDeposit, onDismissDeposit] = useModal(
    <DepositModal
      max={tokenBalance}
      decimals={bank.depositToken.decimal}
      onConfirm={(amount) => {
        if (Number(amount) <= 0 || isNaN(Number(amount))) return;
        onStake(amount);
        onDismissDeposit();
      }}
      tokenName={bank.depositTokenName}
      bank={bank}
    />,
  );

  const [onPresentZap, onDissmissZap] = useModal(
    <ZapModal
      decimals={bank.depositToken.decimal}
      onConfirm={(zappingToken, tokenName, amount) => {
        if (Number(amount) <= 0 || isNaN(Number(amount))) return;
        onZap(zappingToken, tokenName, amount);
        onDissmissZap();
      }}
      tokenName={bank.depositTokenName}
    />,
  );

  const [onPresentWithdraw, onDismissWithdraw] = useModal(
    <WithdrawModal
      max={stakedBalance}
      decimals={bank.depositToken.decimal}
      onConfirm={(amount) => {
        if (Number(amount) <= 0 || isNaN(Number(amount))) return;
        onWithdraw(amount);
        onDismissWithdraw();
      }}
      tokenName={bank.depositTokenName}
    />,
  );

  return (
    <Box>
    <div style={{ opacity: "1", willChange: "auto", transform: "none" }}>
      <div style={{ backfaceVisibility: "hidden", transformStyle: "preserve-3d", willChange: "auto", transform: "none" }} tabIndex={0}>
        <div className="relative flex flex-col items-center p-4 rounded-xl border bg-card/60 backdrop-blur text-card-foreground pointer-events-auto transition-all duration-200 hover:shadow-lg group" style={{ minHeight: "320px" }}>
          <StyledCardContentInner>
            <StyledCardHeader>
              {bank.depositTokenName === 'HOG-OS-LP' ? (
                <>
                  <CardIcon>
                    <TokenSymbol symbol="HOG" size={54} />
                    <TokenSymbol symbol="OS" size={54} />
                  </CardIcon>
                </>
              ) : bank.depositTokenName === 'GHOG-OS-LP' ? (
                <>
                  <CardIcon>
                    <TokenSymbol symbol="GHOG" size={54} />
                    <TokenSymbol symbol="OS" size={54} />
                  </CardIcon>
                </>
              ) : (
                <CardIcon>
                  <TokenSymbol symbol={bank.depositTokenName} size={54} />
                </CardIcon>
              )}
              <Value value={getDisplayBalance(stakedBalance, bank.depositToken.decimal)} />
              <span className="text-m-body text-transparent bg-clip-text bg-gradient-sonic-horizontal">
                ≈ ${earnedInDollars}
              </span>
              <span className="text-m-body text-transparent bg-clip-text bg-gradient-sonic-horizontal">
                {bank.depositTokenName} Staked
              </span>
            </StyledCardHeader>
            <StyledCardActions>
              {approveStatus !== ApprovalState.APPROVED ? (
                <button
                  disabled={
                    bank.closedForStaking ||
                    approveStatus === ApprovalState.PENDING ||
                    approveStatus === ApprovalState.UNKNOWN ||
                    isApproving
                  }
                  onClick={onPresentApprove}
                  className='tracking-normal inline-flex whitespace-nowrap backdrop-blur items-center justify-center gap-x-1 py-2 px-6 rounded-full font-bold focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 [&>svg]:stroke-[3px] text-button-foreground bg-button/[0.16] hover:bg-button/[0.24] transition focus-visible:ring-button text-body'
                  style={{ marginTop: '20px' }}
                >
                  {isApproving ? 'Approving...' : `Approve ${bank.depositTokenName}`}
                </button>
              ) : (
                <>
                  <IconButton onClick={onPresentWithdraw}>
                    <RemoveIcon />
                  </IconButton>
                  <StyledActionSpacer />
                  <AddButton
                    disabled={bank.closedForStaking}
                    onClick={() => (bank.closedForStaking ? null : onPresentDeposit())}
                  >
                    <AddIcon />
                  </AddButton>
                </>
              )}
            </StyledCardActions>
          </StyledCardContentInner>
        </div>
        </div>
      </div>
    </Box>
  );
};

const StyledCardHeader = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
`;
const StyledCardActions = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 28px;
  width: 100%;
`;

const StyledActionSpacer = styled.div`
  height: ${(props) => props.theme.spacing[4]}px;
  width: ${(props) => props.theme.spacing[4]}px;
`;

const StyledCardContentInner = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
`;

export default Stake;
